import { useCallback, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';

import { AnalyticsContext } from './analytics-provider';

// Create an analytics hook that we can use with other components.
const useAnalytics = () => {
    const result = useContext(AnalyticsContext);

    if (!result) {
        throw new Error('Context used outside of its Provider!');
    }

    return result;
};

export type TrackCallbackParamsType = {
    props?: {
        action?: string;
        param?: any;
        [key: string]: any;
    };
    source?: string;
    uniqueEventName?: string;
    [key: string]: any;
};

// added the ability to pass in a required tracking params object as generic type
export type TrackCallbackType = <RequiredTrackingParams = Record<string, any>>(
    eventName: string,
    params?: TrackCallbackParamsType & RequiredTrackingParams,
) => Promise<boolean>;

const useTrack = () => {
    const context = useContext(AnalyticsContext);

    const trackCb: TrackCallbackType = useCallback(
        async (eventName, params) => {
            if (!context?.track) {
                return false;
            }
            const actionName = params?.props?.action ?? '';
            await context.track(eventName + actionName, {
                source: params?.source === '/' ? 'frontpage' : params?.source,
                ...params,
            });
            return true;
        },
        [context],
    );

    return trackCb;
};

const usePage = () => {
    return useContext(AnalyticsContext).page;
};

const useIdentify = () => {
    return useContext(AnalyticsContext).identify;
};

const useReset = () => {
    return useContext(AnalyticsContext).reset;
};

const usePageView = ({ meta }: { meta: any }) => {
    const page = usePage();
    const router = useRouter();

    const onPageView = useCallback(
        (basePath: string, fullPath: string, metadata?: any) => {
            if (process.env.NODE_ENV !== 'production') {
                console.log(`onPageView: ${basePath}`, metadata);
            }

            if (metadata?.authors || metadata?.tags || metadata?.categories) {
                page({
                    basePath,
                    path: fullPath,
                    title: metadata?.title ?? undefined,
                    author: (metadata?.authors || []).join(','),
                    authors: metadata?.authors || [],
                    tags: metadata?.tags || [],
                    categories: metadata?.categories || [],
                    article_modified_time: metadata?.article_modified_time ?? undefined,
                    article_published_time: metadata?.article_published_time ?? undefined,
                });

                return;
            }

            page({ basePath, path: fullPath });
        },
        [router.asPath],
    );

    useEffect(() => {
        onPageView(router.asPath, router.pathname, meta);
    }, [router.asPath]);
};

export { useAnalytics, useIdentify, usePage, usePageView, useReset, useTrack };
